import { ApolloQueryResult } from 'apollo-client';

import {
    getValueFromApiFormat,
    mapAdditionalFilterToApiFormat,
    mapDateRangePresetToApiFormat,
    mapSortParamsToString,
    mapTotalStatsToApiFormat
} from '../../mappers/common/common';
import {
    mapGroupFilterToApiFormat,
    mapRowsToGroups
} from '../../mappers/group/group';
import { getDefaultSort } from '../../service/GridHelpers/GridHelpers';
import { ClientInfo } from '../../typings/common';
import {
    GdStatRequirementsInput,
    GroupsQuery,
    GroupsSuggestQuery
} from '../../typings/graphql-grid';
import {
    defaultClientInfo,
    GridParams,
    GridSourceParams
} from '../../typings/grid';

export const mapSourceParamsToGroupsAndSuggestQueryVariables = (
    params: GridParams,
    clientInfo: ClientInfo
) => {
    const {
        sortParams,
        statPeriod,
        filter,
        view,
        additionalFilter,
        goalIds,
        alertMessageFilter,
        campaignsCalcTypeFilter
    } = params;

    let apiFormatFilter;

    if (params.messageType && alertMessageFilter) {
        apiFormatFilter = mapGroupFilterToApiFormat(
            alertMessageFilter,
            clientInfo,
            campaignsCalcTypeFilter
        );
    } else if (filter) {
        apiFormatFilter = mapGroupFilterToApiFormat(
            filter,
            clientInfo,
            campaignsCalcTypeFilter
        );
    } else {
        apiFormatFilter = {};
    }

    if (additionalFilter !== undefined) {
        apiFormatFilter.archived = mapAdditionalFilterToApiFormat(
            additionalFilter
        );
    }

    let statRequirements: GdStatRequirementsInput = {
        goalIds: goalIds
    };
    if (statPeriod) {
        const { statPreset, statFrom, statTo } = statPeriod;
        statRequirements = {
            ...statRequirements,
            preset: statPreset && mapDateRangePresetToApiFormat(statPreset),
            from: statFrom,
            to: statTo
        };
    }

    return {
        orderBy: sortParams
            ? mapSortParamsToString(sortParams)
            : getDefaultSort(view),
        statRequirements,
        filter: {
            ...((filter || additionalFilter) && apiFormatFilter),
            campaignIdIn: params.campaignsIds
        }
    };
};

export const mapGroupQueryResultToEntitiesQueryResponse = (
    result: ApolloQueryResult<GroupsQuery>,
    params: GridSourceParams
) => {
    const { client, reqId } = result.data;
    const {
        adGroups: groupsResult,
        features,
        info: { workCurrency, countryRegionId }
    } = client;

    const {
        rowset: groupsRows,
        totalCount,
        adGroupIds,
        cacheKey,
        totalStats
    } = groupsResult;

    const { cost, clicks, shows, ctr } = totalStats;

    return {
        data: {
            data: mapRowsToGroups(groupsRows, client, params),
            total: totalCount,
            cacheKey: getValueFromApiFormat(cacheKey),
            allItemsIds: adGroupIds.map(String)
        },
        clientInfo: {
            ...defaultClientInfo,
            ...features,
            workCurrency,
            countryRegionId
        },
        totalStats: mapTotalStatsToApiFormat({
            cost,
            clicks,
            shows,
            ctr
        }),
        hasObjectsOverLimit: groupsResult.features.hasAdGroupsOverLimit,
        reqId
    };
};

export const mapGroupSuggestQueryResultToSuggestQueryResponse = (
    result: ApolloQueryResult<GroupsSuggestQuery>
) => {
    return {
        data: result.data.client.adGroups.rowset.map(item => item.name),
        reqId: result.data.reqId
    };
};
